import React, { useEffect, useRef, useState } from "react";
import Script from "react-load-script";
import classnames from "classnames";

import "./AutodeskViewer.css";

type AutodeskViewerProps = {
  asset: Asset;
  version?: string;
  onDocumentLoad?: (doc: any, views: any[]) => void;
  onDocumentError?: (errorCode: Autodesk.Viewing.ErrorCodes) => void;
  onViewerError?: (errorCode: Autodesk.Viewing.ErrorCodes) => void;
  onModelLoad?: (viewer: any, model: any) => void;
  onModelError?: (errorCode: Autodesk.Viewing.ErrorCodes) => void;
};

const AUTODESK_DIV_ID = "mv-autodesk-viewer";

export const AutodeskViewer: React.FunctionComponent<AutodeskViewerProps> = (
  props
) => {
  const { asset, version, ...rest } = props;

  const viewerDiv = useRef<HTMLDivElement>(null);
  const [viewer, setViewerApp] = useState<
    //@ts-ignore Not provided by @types/forge-viewer
    Autodesk.Viewing.Private.GuiViewer3D
  >(null);
  const [isInitialized, setInitialized] = useState(false);

  // Flags
  const [enable, setEnable] = useState(false);
  const [error, setError] = useState(false);
  const [empty, setEmpty] = useState(true);

  const docs = [asset.media.autodeskUrn];
  const [views, setViews] = useState<any>(null);

  useEffect(() => {
    console.debug({
      enable,
      error,
      empty,
    });
  }, [enable, error, empty]);

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    renderDocument();
  }, [isInitialized, asset]);

  useEffect(() => {
    if (!views) {
      clearViews();
    } else {
      renderViews(views);
    }
  }, [views]);

  const handleLoadModelSuccess = (model: any) => {
    console.info("Model successfully loaded by Forge Viewer.", model);

    if (props.onModelLoad) props.onModelLoad(viewer, model);
  };

  const handleLoadModelError = (errorCode: Autodesk.Viewing.ErrorCodes) => {
    setError(true);

    console.error("Error loading Forge model - errorCode:" + errorCode);
    if (props.onModelError) props.onModelError(errorCode);
  };

  const loadView = (view: any) => {
    console.debug("loading view:", view.guid);
    views[view.viewableID] = view;

    let svfUrl = view.doc.getViewablePath(view);
    let successHandler = handleLoadModelSuccess;
    let errorHandler = handleLoadModelError;
    let modelOptions = {
      sharedPropertyDbPath: view.doc.getFullPath(),
    };

    //load the specified model
    viewer.loadModel(svfUrl, modelOptions, successHandler, errorHandler);
  };

  const renderViews = (list: []) => {
    //check to see if views were added or removed from existing list
    let existing = Object.assign({}, views);
    let incremental: any[] = [];
    list.forEach((view: { viewableID: string }) => {
      if (existing.hasOwnProperty(view.viewableID))
        //the view was previously in the list
        delete existing[view.viewableID];
      else {
        //the view is newly added to the list
        incremental.push(view);
      }
    });

    //anything left in old's keys should be unloaded
    let keys = Object.keys(existing);
    if (keys.length > 0) {
      //views were removed, so restart viewer for lack of 'unload'
      if (viewer) {
        viewer.tearDown();
        viewer.start();
        list.forEach((view) => {
          loadView(view);
        });
      }
    } else {
      //load views incrementally rather than a complete teardown
      incremental.forEach((view) => {
        loadView(view);
      });
    }
  };

  const clearViews = () => {
    console.info("clearing all views.");
    if (viewer) {
      //restart viewer, for lack of ability to unload models
      viewer.tearDown();
      viewer.start();
      setViews(null);
    }
  };

  const clearErrors = () => {
    setError(false);
  };

  const handleLoadDocumentSuccess = (doc: any) => {
    console.info("Forge viewer has successfully loaded document:", doc);

    let views = doc.getRoot().search({ type: "geometry" }, true);
    //augment viewables with the doc they came from
    views.forEach((viewable: any) => {
      viewable.doc = doc;
    });
    setViews(views);

    // //raise an event so caller can select a viewable to display
    if (props.onDocumentLoad) props.onDocumentLoad(doc, views);
  };

  const handleLoadDocumentError = (errorCode: Autodesk.Viewing.ErrorCodes) => {
    setError(true);

    console.error("Error loading Forge document - errorCode:" + errorCode);
    if (props.onDocumentError) props.onDocumentError(errorCode);
  };

  const handleViewerError = (errorCode: Autodesk.Viewing.ErrorCodes) => {
    setError(true);

    console.error("Error loading Forge Viewer. - errorCode:", errorCode);
    if (props.onViewerError) props.onViewerError(errorCode);
  };

  const handleViewerInit = async () => {
    console.info("Forge Viewer has finished initializing.");

    let container = viewerDiv.current;

    // Create Viewer instance so we can load models.
    //@ts-ignore Not provided by @types/forge-viewer
    const viewer = new Autodesk.Viewing.Private.GuiViewer3D(container);

    console.info("Starting the Forge Viewer...");
    var errorCode = viewer.start();
    if (!errorCode) {
      console.info("Forge Viewer has successfully started.");
      setEnable(true);
      renderDocument();
    } else {
      console.error("Error starting Forge Viewer - code:", errorCode);
      handleViewerError(errorCode);
    }
    setViewerApp(viewer);
    setInitialized(true);
  };

  const renderDocument = () => {
    if (viewer) {
      clearErrors();
      console.info("reviewing documents...");
      setEmpty(docs.length == 0);
      docs.forEach((urn) => {
        console.log("Forge Viewer is loading document:", urn);

        Autodesk.Viewing.Document.load(
          `urn:${urn}`,
          handleLoadDocumentSuccess,
          handleLoadDocumentError
        );
      });
    }
  };

  const handleScriptLoad = () => {
    console.log("Autodesk scripts have finished loading.");

    let options = {
      endpoint: 'https://cdn.derivative.autodesk.com',
      env: "AutodeskProduction",
      useADP: false,
      getAccessToken: function (
        onGetAccessToken: (
          accessToken: string,
          expireTimeSeconds: number
        ) => void
      ) {
        if (!asset.autodeskPayload) {
          return;
        }
        onGetAccessToken(
          asset.autodeskPayload.access_token,
          asset.autodeskPayload.expires_in
        );
      },
    };

    Autodesk.Viewing.Initializer(options, handleViewerInit);
  };

  useEffect(() => {
    return () => {
      console.log("React Forge Viewer unmounting...");
      if (viewer) {
        viewer.tearDown();
        viewer.finish();
        console.log("React Forge Viewer destroyed.");
      }
    };
  }, []);

  const classNames = classnames(
    "mv-asset-viewer__asset",
    "mv-asset-viewer__asset--autodesk"
  );

  if (!asset.autodeskPayload) {
    return null;
  }

  return (
    <div className="mv-asset-viewer mv-asset-viewer__container">
      <div ref={viewerDiv} id={AUTODESK_DIV_ID} className={classNames}></div>
      <link
        rel="stylesheet"
        type="text/css"
        href={`https://developer.api.autodesk.com/modelderivative/v2/viewers/style.min.css?v=v${version}`}
      />
      <Script
        url={`https://developer.api.autodesk.com/modelderivative/v2/viewers/viewer3D.min.js?v=v${version}`}
        onLoad={handleScriptLoad}
        onError={handleViewerError}
      />
    </div>
  );
};

AutodeskViewer.defaultProps = {
  version: "7.14.0",
};

export default AutodeskViewer;
